/**
 * The counts beside `Incidents` and `Recommendations` in the menu.
 *
 * Pure, and separate from the component, because every rule here is a silent wrong answer when
 * inverted — a badge cannot report its own mistake, and a reader trusts a number in a menu more
 * than almost anything else on the screen.
 */

/** Which attention count a menu item carries. Never the number. */
export type NavCountKey = 'incidents' | 'recommendations';

/**
 * What the two badges read, as the fleet summary reports them.
 *
 * ⚠️ **`null` MEANS NOTHING IS KNOWN, and it arrives three different ways — not loaded, not
 * entitled, or WITHHELD.** All three must draw the same: an absent badge. They cannot be told
 * apart in a menu and pretending otherwise would need wording there is no room for.
 */
export interface NavCounts {
  /** Incidents OPEN at this moment, whenever raised. Point-in-time. */
  incidents: number | null;
  /**
   * Recommendations nobody has decided on yet — `GENERATED`. Point-in-time.
   *
   * ⚠️ **NOT `awaitingApproval`, which is what shipped and was wrong.** That counts only advice
   * needing a privileged decision, so a queue of three showed a badge of 1 and the row it
   * dropped was `New`. Reported from the product.
   */
  recommendations: number | null;
}

export const EMPTY_NAV_COUNTS: NavCounts = { incidents: null, recommendations: null };

/**
 * The window the badge asks for.
 *
 * ⚠️ **IT CHANGES NEITHER FIGURE, and that is why a constant is safe here.** `openNow` and
 * `undecidedRecommendations` are POINT-IN-TIME — "open at this moment, regardless of when they were
 * raised" — so the window governs only the period figures beside them, which the menu does not
 * read. What the constant buys is a shared cache: 168 is `FLEET_SUMMARY_DEFAULT_HOURS`, which is
 * the dashboard's own default and the AI Assistant rail's fixed value, so on both of those pages
 * the badge costs NOTHING. A reader who has moved the dashboard to another period pays one small
 * request, which is the honest trade for never showing a number that moved when they changed a
 * control they were not looking at.
 */
export const NAV_COUNT_HOURS = 168;

/**
 * What to draw beside a menu item: a number, or nothing at all.
 *
 * ⚠️ **ZERO DRAWS NOTHING, and that is not the same decision as hiding a null.** A badge reading
 * `0` is a control claiming to carry information while carrying none — and worse, it looks
 * identical to a badge that has failed to load, so the one state a reader should be able to
 * trust ("there is nothing outstanding") becomes indistinguishable from "we could not ask". The
 * absence IS the all-clear, which is how every menu badge anybody has used already behaves.
 *
 * ⚠️ **AND A NEGATIVE DRAWS NOTHING, though nothing can produce one today.** The contract says
 * `z.number().int().nullable()` with no lower bound, so a count that went negative would render
 * as `-3` beside a menu item. Refusing it here costs one comparison and cannot be reached by a
 * later change to the server.
 */
export function badgeValue(count: number | null | undefined): number | null {
  /*
   * ⚠️ **THIS LINE IS A NET THAT CANNOT FIRE, and saying so is better than presenting it as
   * coverage.** `Number.isFinite(null)` is `false` — it does not coerce — so the numeric guard
   * below already returns for both. Deleting this line was run as a mutation and the suite
   * stayed GREEN, which is the honest result rather than a gap: an assertion over real inputs
   * cannot reach a branch that is redundant, and the only way to test one is to create the state
   * it is redundant against. It stays because it is the SEMANTIC statement — `null` means "not
   * known", which is a different fact from "not a positive number" — and because the two guards
   * would have to be edited together for it to matter.
   */
  if (count === null || count === undefined) return null;
  if (!Number.isFinite(count) || count <= 0) return null;
  return Math.floor(count);
}

/**
 * Above this, the badge reads `99+`.
 *
 * A four-digit number in a 220px menu either wraps the row or pushes the label out of line, and
 * the precise figure is on the page it points at. `99+` is the width the layout is built for.
 */
export const NAV_COUNT_CAP = 99;

/** The text on the badge. `null` when there is no badge. */
export function badgeLabel(count: number | null | undefined): string | null {
  const n = badgeValue(count);
  if (n === null) return null;
  return n > NAV_COUNT_CAP ? `${NAV_COUNT_CAP}+` : String(n);
}

/**
 * What a reader is told the badge MEANS, on hover and to a screen reader.
 *
 * ⚠️ **IT NAMES THE COUNT, because the badge and the page it opens legitimately disagree.** The
 * queues are unfiltered by default — measured, 47 recommendations behind a badge of 4 — so
 * "4" alone invites the reading "this page has four rows", which is false and is exactly the
 * "a figure counting one set and a destination showing another" fault this platform has paid
 * for on the dashboard tiles. The sentence is the whole mitigation.
 *
 * It states the EXACT number rather than the capped label: a tooltip has room, and `99+` is a
 * layout concession rather than something a reader should have to accept when they have asked.
 */
export function badgeDescription(key: NavCountKey, count: number | null | undefined): string | null {
  const n = badgeValue(count);
  if (n === null) return null;
  return key === 'incidents'
    ? `${n} open now`
    : `${n} awaiting a decision`;
}
